/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, AppDispatch } from 'helpers/store'
import LeadsAction from 'store/leads/LeadsAction'
import { Lead } from 'types/entities/Lead'
import { ReminderModelName } from 'types/enums/ReminderModelName'
import { createAssignmentReminderForEntity } from 'utils/reminders/createAssignmentReminder'

type LeadInlinePatch = Partial<Pick<Lead, 'status' | 'category' | 'seller_id' | 'tags'>>

const fallbackLeadName = (lead: Lead): string =>
    lead.name || lead.company_name || lead.full_name || lead.contact_person || `Lead #${lead.id}`

export const buildLeadCreatePayload = (lead: Lead, patch: LeadInlinePatch): Partial<Lead> => ({
    id: lead.id,
    hall_id: lead.hall_id,
    name: fallbackLeadName(lead),
    seller_id: patch.seller_id ?? lead.seller_id,
    ...patch,
})

export const useInlineLeadPillUpdate = () => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()

    const updateLeadInline = useCallback(
        async (lead: Lead, patch: LeadInlinePatch) => {
            await dispatch(LeadsAction.fetchCreateOrUpdateLead(buildLeadCreatePayload(lead, patch)))

            if (patch.seller_id != null && Number(patch.seller_id) !== Number(lead.seller_id)) {
                await createAssignmentReminderForEntity(dispatch as unknown as AppDispatch, {
                    modelName: ReminderModelName.Lead,
                    modelId: Number(lead.id),
                    sellerId: Number(patch.seller_id),
                    previousSellerId: lead.seller_id,
                    hallId: lead.hall_id,
                    content: t('Assigned to you') as string,
                })
            }
        },
        [dispatch, t]
    )

    return { updateLeadInline }
}
